import { Avatar, InteractiveContainer, Popover } from "@jugl-web/ui-components";
import { cx, useSearchInput, useTranslations } from "@jugl-web/utils";
import { useEntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { CreateNewEntityButton } from "@web-src/modules/entities/components/CreateNewEntityButton";
import { EntityCard } from "@web-src/modules/entities/components/EntityCard";
import { EntityInvitationCard } from "@web-src/modules/entities/components/EntityInvitationCard";
import {
  LeaveEntityConfirmationDialogsFlow,
  LeaveEntityConfirmationDialogsFlowHandle,
} from "@web-src/modules/entities/components/LeaveEntityConfirmationDialogsFlow";
import { CreateCXWorkspaceDialog } from "@web-src/modules/cx/components/CreateCXWorkspaceDialog";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { saveItemToStorage, getStorageItem } from "@jugl-web/utils/storage";
import { useTheme } from "@web-src/themes/useTheme";
import { SearchInput } from "@jugl-web/ui-components/cross-platform/SearchInput";
import { UserRole } from "@jugl-web/rest-api";
import { useNavigateToEntityById } from "@web-src/features/app/hooks/useNavigateToEntityById";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { ReactComponent as ChevronUpIcon } from "./assets/chevron-up.svg";
import { InvitationAlert } from "../InvitationAlert/InvitationAlert";
import { EntityInvitesInfoItem } from "../EntityInvitesInfoItem/EntityInvitesInfoItem";
import { SelectProductView } from "./components/SelectProductView";

type PopoverView = "list" | "selectProduct";

export const SelectEntityPopover: React.FC = () => {
  const { entity } = useEntitySelectedProvider();
  const [isInvitationAlertOpen, setIsInvitationAlertOpen] = useState(false);
  const [popoverView, setPopoverView] = useState<PopoverView>("list");
  const [isCXDialogOpen, setIsCXDialogOpen] = useState(false);

  const suppressInvitationsOnWorkspaceFeatures =
    window.location.pathname.includes("workspace-features");
  const navigateToEntity = useNavigateToEntityById();
  const { navigateToPage } = useNavigation();

  const { tcx } = useTheme();
  const { inputProps } = useSearchInput();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    entitiesState: { entities },
    entityInvitesState: { entityInvites },
  } = useEntitiesListProvider();
  const { t } = useTranslations();
  const createEntityLimitReached = useMemo(() => {
    if (!entities) {
      return false;
    }
    return entities?.filter((e) => e.role === UserRole.admin).length >= 5;
  }, [entities]);
  const reorderedEntities = useMemo(() => {
    const otherEntities = (entities || []).filter((e) => e.id !== entity.id);
    return [entity, ...otherEntities];
  }, [entities, entity]);

  const filteredEntities = useMemo(() => {
    if (!searchQuery) return reorderedEntities;
    return reorderedEntities.filter((e) =>
      e.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [reorderedEntities, searchQuery]);

  const filteredEntityInvites = useMemo(() => {
    if (!searchQuery) return entityInvites || [];
    return (
      entityInvites?.filter((invite) =>
        invite.entityName?.toLowerCase().includes(searchQuery.toLowerCase())
      ) || []
    );
  }, [entityInvites, searchQuery]);

  const hasUnreadInDifferentEntity = useMemo(
    () => entities?.some((entityItem) => entityItem.unread),
    [entities]
  );

  const leaveEntityConfirmationDialogsFlowRef =
    useRef<LeaveEntityConfirmationDialogsFlowHandle | null>(null);

  const shouldShowSearch =
    (entities?.length ?? 0) + (entityInvites?.length ?? 0) > 6;

  useEffect(() => {
    if (!entityInvites?.length) return;
    const shownInvites =
      getStorageItem<string[]>("JUGL__ENTITY_INVITATIONS_SHOWN") || [];
    const hasUnshownInvites = entityInvites.filter(
      (invite) => !shownInvites.includes(invite.id)
    );
    if (
      hasUnshownInvites.length > 0 &&
      !suppressInvitationsOnWorkspaceFeatures
    ) {
      setIsInvitationAlertOpen(true);
      saveItemToStorage(
        "JUGL__ENTITY_INVITATIONS_SHOWN",
        entityInvites.map((invite) => invite.id)
      );
    }
  }, [entityInvites, suppressInvitationsOnWorkspaceFeatures]);

  return (
    <>
      <InvitationAlert
        isOpen={isInvitationAlertOpen}
        onClose={() => setIsInvitationAlertOpen(false)}
      />
      <Popover
        className="font-secondary z-[51] ml-10 mt-[-8px]"
        placement="bottom-start"
        onUnmount={() => setPopoverView("list")}
        renderTrigger={({ Trigger, triggerRef, isOpen }) => (
          <Trigger
            as={InteractiveContainer}
            ref={triggerRef}
            data-id="select-entity-popover-trigger"
            className={tcx(
              "font-secondary hover:bg-primary-900 flex h-full items-center bg-transparent px-6 text-sm font-medium text-white outline-none duration-300 ease-out",
              {
                "bg-primary-900": isOpen,
                light: [
                  "hover:bg-primary-700",
                  {
                    "bg-[#1A75D2]": isOpen,
                  },
                ],
                dark: [
                  "hover:bg-[#333333]",
                  {
                    "bg-[#333333]": isOpen,
                  },
                ],
                blue: [
                  "hover:bg-primary-900",
                  {
                    "bg-primary-900": isOpen,
                  },
                ],
              }
            )}
          >
            <div className="relative mr-3 h-6 w-6">
              <div>
                <Avatar
                  size="sm"
                  username={entity.name}
                  imageUrl={entity.logoImg}
                  className="border-[0.2px] border-solid border-white"
                />
              </div>
              {hasUnreadInDifferentEntity && (
                <div className="absolute right-[2px] top-[0px] flex translate-x-1/2 items-center justify-center rounded-md">
                  <div className="bg-gradients-danger h-[8px] w-[8px] rounded-full border-[1px] border-solid border-white" />
                </div>
              )}
            </div>
            <div
              className="font-secondary mr-1 max-w-[300px] truncate text-sm font-medium text-white"
              title={entity.name}
            >
              {entity.name}
            </div>
            <ChevronUpIcon
              className={cx("shrink-0 transition-transform", {
                "rotate-180": isOpen,
              })}
            />
            <div className="pl-3">
              <EntityInvitesInfoItem />
            </div>
          </Trigger>
        )}
      >
        {({ onClose }) =>
          popoverView === "selectProduct" ? (
            <SelectProductView
              onBack={() => setPopoverView("list")}
              onSelectPM={() => {
                onClose();
                setPopoverView("list");
                navigateToPage(
                  "authNewWorkspace",
                  { flow: "inapp" },
                  { queryParams: { skipSelectProduct: "true" } }
                );
              }}
              onSelectCX={() => {
                onClose();
                setPopoverView("list");
                setIsCXDialogOpen(true);
              }}
            />
          ) : (
            <div className="max-h-[min(720px,90vh)] w-[440px] overflow-y-auto p-4">
              {shouldShowSearch && (
                <div className="pb-4">
                  <SearchInput
                    {...inputProps}
                    onChange={(e) => setSearchQuery(e.currentTarget.value)}
                    value={searchQuery}
                    variant="filled"
                    color="grey"
                    onClear={() => setSearchQuery("")}
                  />
                </div>
              )}
              <div
                className={cx("text-dark pb-4 font-medium", {
                  "border-dark-100 border-0 border-t-[1px] border-solid pt-4":
                    shouldShowSearch,
                })}
              >
                {filteredEntityInvites.length
                  ? t({
                      id: "entity-popover.pending-invitations",
                      defaultMessage: "Pending Invitations",
                    })
                  : t({
                      id: "common.workspaces",
                      defaultMessage: "Workspaces",
                    })}
              </div>
              <div className="flex flex-col gap-1.5">
                {filteredEntityInvites?.map((invitation) => (
                  <EntityInvitationCard
                    invitation={invitation}
                    key={invitation.id}
                    isArrowHidden
                    onInvitationAcceptedSuccess={() => {
                      navigateToEntity(
                        invitation.entityId,
                        invitation.type === "org"
                      );
                    }}
                  />
                ))}
              </div>
              {filteredEntityInvites?.length ? (
                <div className="w-full py-4">
                  <div className="bg-dark-100 h-[1px] w-full" />
                </div>
              ) : null}

              {filteredEntities.length > 0 &&
                filteredEntityInvites.length > 0 && (
                  <div className="text-dark pb-4 font-medium">
                    {t({
                      id: "common.workspaces",
                      defaultMessage: "Workspaces",
                    })}
                  </div>
                )}
              <div className="flex flex-col gap-1.5">
                {!searchQuery && (
                  <CreateNewEntityButton
                    isLimitReached={createEntityLimitReached}
                    onCreateClick={() => setPopoverView("selectProduct")}
                  />
                )}
                {filteredEntities.length === 0 &&
                  filteredEntityInvites.length === 0 && (
                    <div className="text-grey w-full text-center text-sm">
                      {t({
                        id: "common.no-results",
                        defaultMessage: "No results",
                      })}
                    </div>
                  )}
                {filteredEntities.map((entityItem) => (
                  <EntityCard
                    key={entityItem.id}
                    entity={entityItem}
                    onLeaveEntity={() => {
                      onClose();
                      leaveEntityConfirmationDialogsFlowRef.current?.triggerLeaveEntityFlow(
                        entityItem
                      );
                    }}
                    onDeleteEntity={() => {
                      onClose();
                      leaveEntityConfirmationDialogsFlowRef.current?.triggerDeleteEntityFlow(
                        entityItem
                      );
                    }}
                    isSelected={entityItem.id === entity.id}
                    variant="popover"
                    showToastOnSwitch
                    onEntitySwitch={() => onClose()}
                  />
                ))}
              </div>
            </div>
          )
        }
      </Popover>
      <LeaveEntityConfirmationDialogsFlow
        ref={leaveEntityConfirmationDialogsFlowRef}
      />
      <CreateCXWorkspaceDialog
        isOpen={isCXDialogOpen}
        onRequestClose={() => setIsCXDialogOpen(false)}
      />
    </>
  );
};
